import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuthPermissions, getAuthPermissions } from '../api/studykit';
import { StudyPageContext } from '../App';

const INITIAL = {
  globalPermissions: [],
  studyPermissions: {},
};

type AuthorizationContextState = {
  hasPermission: (...permission: string[]) => boolean;
  hasPermissionsForStudy(studyId: string, permissions: string[]): boolean;
};

export const AuthorizationContext = React.createContext<AuthorizationContextState>(
  {} as AuthorizationContextState,
);

export const AuthorizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authz, setAuthz] = useState<AuthPermissions>(INITIAL);
  const studyCtx = useContext(StudyPageContext);
  const studyId = studyCtx?.study.id;

  useEffect(() => {
    getAuthPermissions().then(setAuthz);
  }, []);

  const hasPermission = useCallback(
    (...permissions: string[]) => {
      const userPermissions = [
        ...authz.globalPermissions,
        ...(authz.studyPermissions[studyId] || []),
      ];
      return permissions.every((p) => userPermissions.includes(p));
    },
    [authz, studyId],
  );
  const hasPermissionsForStudy = useCallback(
    (studyId: string, permissions: string[]) => {
      const userPermissions = [
        ...authz.globalPermissions,
        ...(authz.studyPermissions[studyId] || []),
      ];
      return permissions.every((p) => userPermissions.includes(p));
    },
    [authz],
  );

  return (
    <AuthorizationContext.Provider
      value={{
        hasPermission,
        hasPermissionsForStudy,
      }}
    >
      {children}
    </AuthorizationContext.Provider>
  );
};

export const useAuthorization = () => useContext(AuthorizationContext);

export const usePermission = (...permissions: string[]) => {
  const { hasPermission } = useAuthorization();
  return hasPermission(...permissions);
};

export const HasPermission: React.FC<{ value: string | string[]; children: React.ReactNode }> = ({
  value,
  children,
}) => {
  const { hasPermission } = useAuthorization();
  const permissionsToCheck = typeof value === 'string' ? [value] : value;

  return <>{hasPermission(...permissionsToCheck) && children}</>;
};
